import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tooltip,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import api from '../api';
import { formatFen } from '../utils/money';

export default function ProfitReport() {
  const [profitData, setProfitData] = useState([]);
  const [loading, setLoading] = useState(true);
  // 财务页底线：加载失败必须显式报错+重试，渲染成"全 0 利润表"比报错危险得多
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetchProfitData();
  }, []);

  const fetchProfitData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await api.get('/api/reports/profit');
      // 后端契约：{ projects: [...], summary: {...} }
      const data = response.data;
      const projects = Array.isArray(data) ? data : (data?.projects ?? []);
      setProfitData(projects);
    } catch (error) {
      console.error('Error fetching profit data:', error);
      setLoadError(error.response?.data?.error || '利润数据加载失败，请检查网络后重试');
    } finally {
      setLoading(false);
    }
  };


  const getStatusText = (status) => {
    switch (status) {
      case 0: return '未开工';
      case 1: return '施工中';
      case 2: return '已完工';
      case 3: return '已结算';
      case 4: return '暂停';
      default: return '未知';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return 'default';
      case 1: return 'primary';
      case 2: return 'success';
      case 3: return 'warning';
      case 4: return 'error';
      default: return 'default';
    }
  };

  // 垫资生意核心字段：已收款、未收款、实现利润（已收-成本，反映垫资回收）
  const totals = profitData.reduce((acc, item) => ({
    contractAmount: acc.contractAmount + (item.contractAmount || 0),
    totalCost: acc.totalCost + (item.totalCost || 0),
    totalIncome: acc.totalIncome + (item.totalIncome || 0),
    contractProfit: acc.contractProfit + (item.contractProfit || 0),
    realizedProfit: acc.realizedProfit + (item.realizedProfit || 0),
    uncollected: acc.uncollected + (item.uncollected || 0),
  }), {
    contractAmount: 0,
    totalCost: 0,
    totalIncome: 0,
    contractProfit: 0,
    realizedProfit: 0,
    uncollected: 0,
  });

  // 合同回收率 = 已收款 / 合同额（垫资老板最关心的回收进度）
  const collectionRate = totals.contractAmount > 0
    ? (totals.totalIncome / totals.contractAmount * 100).toFixed(1)
    : '0.0';

  // 垫资未回收预警：实现利润为负 = 已收款没盖住成本
  const negativeProjects = profitData.filter((i) => (i.realizedProfit || 0) < 0);
  const negativeTotal = negativeProjects.reduce((s, i) => s + i.realizedProfit, 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <Alert severity="error">{loadError}</Alert>
        <Button variant="contained" onClick={fetchProfitData}>重试</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>项目利润</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        垫资回收视图 · 重点关注「已收款 / 未收款 / 实现利润」
      </Typography>

      {negativeProjects.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>垫资未回收预警：</strong>
          有 {negativeProjects.length} 个项目「实现利润」为负（已收款未覆盖成本），合计 {formatFen(negativeTotal)}，请尽快催收尾款。
        </Alert>
      )}

      {/* 三大核心指标：已收款 / 未收款 / 实现利润 */}
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>已收款（已收回）</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {formatFen(totals.totalIncome)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                合同回收率 {collectionRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>未收款（垫资占用）</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {formatFen(totals.uncollected)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                合同额 − 已收款
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid', borderColor: totals.realizedProfit < 0 ? 'error.main' : 'success.main' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>实现利润（已收 − 成本）</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: totals.realizedProfit < 0 ? 'error.main' : 'success.main' }}>
                {formatFen(totals.realizedProfit)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                真实赚到 · 反映垫资是否回收
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 参考指标：合同总额 / 总成本 / 合同毛利（按合同额，仅供参考） */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>合同总额</Typography>
              <Typography variant="h6">{formatFen(totals.contractAmount)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>总成本</Typography>
              <Typography variant="h6">{formatFen(totals.totalCost)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Tooltip
                title="按合同额计算的毛利（合同额−成本），并非已实现收益。垫资回收情况请以「实现利润（已收−成本）」为准。"
                arrow
              >
                <Typography color="text.secondary" gutterBottom sx={{ cursor: 'help' }}>
                  合同毛利（按合同额·参考）
                </Typography>
              </Tooltip>
              <Typography variant="h6" color={totals.contractProfit < 0 ? 'error.main' : 'text.primary'}>
                {formatFen(totals.contractProfit)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {profitData.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无项目数据
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>项目名称</TableCell>
                <TableCell>状态</TableCell>
                <TableCell align="right">合同金额</TableCell>
                <TableCell align="right">总成本</TableCell>
                <TableCell align="right">已收款</TableCell>
                <TableCell align="right">未收款</TableCell>
                <TableCell align="right">实现利润</TableCell>
                <TableCell align="right">
                  合同毛利
                  <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 400 }}>（按合同额）</Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profitData.map((item) => {
                const isNegative = (item.realizedProfit || 0) < 0;
                const hasUncollected = (item.uncollected || 0) > 0;
                return (
                  <TableRow key={item.projectId}>
                    <TableCell>{item.projectName}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(item.status)}
                        color={getStatusColor(item.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{formatFen(item.contractAmount)}</TableCell>
                    <TableCell align="right">{formatFen(item.totalCost)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatFen(item.totalIncome)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: hasUncollected ? 'warning.main' : 'text.secondary' }}>
                      {formatFen(item.uncollected)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 'bold',
                        color: isNegative ? 'error.main' : 'success.main',
                        backgroundColor: (theme) => isNegative ? alpha(theme.palette.error.main, 0.08) : 'inherit',
                      }}
                    >
                      {formatFen(item.realizedProfit)}
                      {isNegative && (
                        <Chip label="未回收" color="error" size="small" sx={{ ml: 1, height: 20, fontSize: '0.75rem' }} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {formatFen(item.contractProfit)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>合计</TableCell>
                <TableCell></TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.contractAmount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.totalCost)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{formatFen(totals.totalIncome)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{formatFen(totals.uncollected)}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 'bold',
                    color: totals.realizedProfit < 0 ? 'error.main' : 'success.main',
                    backgroundColor: (theme) => totals.realizedProfit < 0 ? alpha(theme.palette.error.main, 0.08) : 'inherit',
                  }}
                >
                  {formatFen(totals.realizedProfit)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>{formatFen(totals.contractProfit)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
